import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Loader2, Upload, Sparkles, X, Image as ImageIcon, UploadCloud } from "lucide-react";

// The renditions a Content actually becomes. Mirrors backend CONTENT_RATIOS, which is
// ["SQUARE", "PORTRAIT"] - landscape was dropped in round 2 and this list was not
// updated with it, so the builder previewed a 16:9 placement that no save has produced
// since. The merchant reviewed three tiles and got two files.
//
// The in-car panel renders the square.
const PLACEMENTS = [
  { key: "SQUARE", label: "Square 1:1", w: 1, h: 1 },
  { key: "PORTRAIT", label: "Portrait 9:16", w: 9, h: 16, extended: true },
];

// The fill options the merchant used to pick from. Gone with the picker (round 2) - the
// server still accepts a fill and uses it only when portrait extension fails, and BLUR is
// the one value that path passes.

// Convert a data: URL (e.g. an AI-generated preview) into a Blob so it can be uploaded
// as a file to /content/upload — we save the exact image the user approved.
function dataURLtoBlob(dataURL) {
  const [head, body] = dataURL.split(",");
  const mime = head.match(/:(.*?);/)?.[1] || "image/png";
  const bin = atob(body);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

// Read a File into a data: URL (base64) so a replacement master can ride along in the
// JSON PATCH body when editing existing Content.
function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

/**
 * @param onUseInCampaign  When set, each content card gains a "Use in my campaign"
 *   action. Round 2: generating an image from a campaign took you here and left you
 *   here - "doesn't link back to campaign, and the campaign draft is gone" - so the
 *   only way back was to rebuild the campaign and pick the content from the library.
 *
 * @param galleryOnly  Render the library as a read-only gallery: no builder, no drafts
 *   toggle, no dialog. This is what /creatives/images shows (round 3, 4.3). Without it
 *   Images would be a second copy of the builder under a different name, which is
 *   exactly what was wrong before Images was removed.
 */
export default function ContentManager({ brandColors = [], onUseInCampaign = null, galleryOnly = false }) {
  const { token } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);

  const [builderOpen, setBuilderOpen] = useState(false);
  const [expandedPlacement, setExpandedPlacement] = useState(null); // preview lightbox
  const [mode, setMode] = useState("upload"); // 'upload' | 'generate'
  const [name, setName] = useState("");
  // Not a control any more (round 2). The server uses it only when portrait
  // extension fails, and BLUR is the least-bad band to fall back to.
  const fillType = "BLUR";
  const [fillColor, setFillColor] = useState(brandColors[0] || "#1d9e75");
  const [masterFile, setMasterFile] = useState(null);
  const [masterPreview, setMasterPreview] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [generatedB64, setGeneratedB64] = useState(null); // AI preview awaiting save
  // A merchant's own image used as the starting point for generation (#1). Held as a
  // data URL because that is what /content/edit-preview takes.
  const [referenceB64, setReferenceB64] = useState(null);
  const [referenceName, setReferenceName] = useState("");
  // The genuine AI-extended portrait, once asked for. Until then the portrait tile shows
  // the square letterboxed and says so - it does NOT pretend to be the output (#20).
  const [portraitB64, setPortraitB64] = useState(null);
  // Only the FAILURE is tracked now (round 9, TP-14). The three-way method was shown
  // on the tile and Joel asked for it to go: extended and generated are both a portrait
  // that came out fine, and which one it was is not the merchant's problem.
  const [portraitPadded, setPortraitPadded] = useState(false);
  const [extending, setExtending] = useState(false);
  const [editInstruction, setEditInstruction] = useState(""); // iterative refine
  const [editingId, setEditingId] = useState(null); // editing an existing Content (null = create)
  const [existingMaster, setExistingMaster] = useState(null); // its master URL (for preview)
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    fetchContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build the portrait as soon as there is a square, so one prompt produces both
  // placements with nothing to click. Round 3: "one image generation prompt should
  // create two images automatically."
  //
  // Only for a GENERATED square. An upload has no prompt to fall back on, so an
  // automatic attempt there would spend a model call to arrive at padding.
  useEffect(() => {
    if (!builderOpen || mode !== "generate") return;
    if (!generatedB64 || portraitB64 || extending) return;
    showRealPortrait();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatedB64, builderOpen, mode]);

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${API}/content`, auth);
      setContent(res.data || []);
    } catch (e) {
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  const resetBuilder = () => {
    setMode("upload"); setName(""); setMasterFile(null); setMasterPreview(null);
    setPrompt(""); setGeneratedB64(null); setEditInstruction("");
    setEditingId(null); setExistingMaster(null);
    // Cleared with the rest, or the reference image from the last creative silently
    // becomes the starting point for the next one (#1).
    setReferenceB64(null); setReferenceName("");
    setPortraitB64(null); setPortraitPadded(false);
  };

  const openBuilder = () => { resetBuilder(); setBuilderOpen(true); };

  // Resolve a stored master/rendition URL (often a relative /api/media/... path) to an
  // absolute URL against the backend, so it loads on the deployed site and localhost.
  const resolveMedia = (url) =>
    !url || url.startsWith("http") || url.startsWith("data:") ? url : `${API.replace(/\/api\/?$/, "")}${url}`;

  const openEditor = (c) => {
    resetBuilder();
    setEditingId(c.id);
    setName(c.name || "");
    setFillColor(c.fill_color || brandColors[0] || "#1d9e75");
    setExistingMaster(resolveMedia(c.master_url));
    setMode("upload");
    setBuilderOpen(true);
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    setMasterFile(f);
    setMasterPreview(URL.createObjectURL(f));
    if (!name) setName(f.name.replace(/\.[^.]+$/, ""));
  };

  const MAX_REFERENCE_MB = 10;

  // The merchant's image becomes the starting point rather than the finished creative
  // (#1). Checked before reading so an oversized file is refused straight away instead
  // of after a base64 encode; the server enforces the same limit regardless.
  const onPickReference = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Choose an image file");
      return;
    }
    if (file.size > MAX_REFERENCE_MB * 1024 * 1024) {
      toast.error(`That image is ${(file.size / 1024 / 1024).toFixed(1)}MB. Use one under ${MAX_REFERENCE_MB}MB.`);
      return;
    }
    setReferenceB64(await fileToDataURL(file));
    setReferenceName(file.name);
    // A previous result was built from something else, so it is no longer what the
    // panel is showing the merchant.
    setGeneratedB64(null);
  };

  const clearReference = () => {
    setReferenceB64(null);
    setReferenceName("");
    setGeneratedB64(null);
  };

  const onGenerate = async () => {
    if (!prompt.trim()) {
      toast.error(referenceB64 ? "Say what to do with your image" : "Enter a prompt to generate");
      return;
    }
    setBusy(true);
    try {
      // With a reference, this is an EDIT of the merchant's image - the same endpoint
      // the Refine loop uses, which has always accepted any image and not only one the
      // AI produced. Without one, generation from text as before.
      const res = referenceB64
        ? await axios.post(`${API}/content/edit-preview`,
            { image_base64: referenceB64, instruction: prompt.trim() }, auth)
        : await axios.post(`${API}/content/generate-preview`,
            { prompt: prompt.trim(), name: name.trim() }, auth);
      const b64 = res.data?.image_base64;
      if (!b64) throw new Error("no image returned");
      setGeneratedB64(b64);
      // A new master means the portrait we extended is of a different image.
      setPortraitB64(null);
      if (!name.trim()) {
        setName(referenceName
          ? referenceName.replace(/\.[^.]+$/, "")
          : `AI - ${prompt.trim().slice(0, 30)}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const onEdit = async () => {
    if (!generatedB64) return;
    if (!editInstruction.trim()) { toast.error("Describe the change you want"); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/content/edit-preview`, { image_base64: generatedB64, instruction: editInstruction.trim() }, auth);
      const b64 = res.data?.image_base64;
      if (!b64) throw new Error("no image returned");
      setGeneratedB64(b64);
      setPortraitB64(null);  // refined master, so the extension is stale
      setEditInstruction("");
      toast.success("Edit applied");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Edit failed");
    } finally {
      setBusy(false);
    }
  };

  // Fetch the portrait the save path would actually produce. Same endpoint, same helper
  // (#20) - the previous preview mocked padding in CSS and so showed the merchant the
  // one outcome the extension exists to avoid.
  // previewSrc is only base64 when generating. An upload gives a blob: URL and saved
  // content a /api/media/ URL, and the server used to base64-decode those into bytes
  // Pillow could not open, which is the 500 on this endpoint. Fetch and convert first.
  const asBase64 = async (src) => {
    if (!src) return null;
    if (src.startsWith("data:")) return src;
    const blob = await (await fetch(src)).blob();
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  };

  const showRealPortrait = async () => {
    if (!previewSrc) return;
    setExtending(true);
    try {
      const source = await asBase64(previewSrc);
      if (!source) throw new Error("no image to send");
      const res = await axios.post(`${API}/content/extend-preview`,
        // The prompt travels with it: when the model will not extend the square, the
        // server generates the portrait from this instead of padding.
        { image_base64: source, instruction: (prompt || "").trim() || "extend to portrait" },
        auth);
      const b64 = res.data?.image_base64;
      if (!b64) throw new Error("no image returned");
      setPortraitB64(b64);
      // Three outcomes, one of which is a failure. Since round 9 (TP-14) only the
      // failure is shown: extended and generated are both a portrait that came out
      // fine, and how it was built is not the merchant's problem.
      const method = res.data?.method || (res.data?.extended ? "extended" : "padded");
      setPortraitPadded(method === "padded");
      if (method === "padded") {
        toast.warning("Could not build a portrait for this one, so it is padded. Try a different image or wording.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not build the portrait");
    } finally {
      setExtending(false);
    }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const body = { name: name.trim(), fill_type: fillType, fill_color: fillColor };
      if (mode === "generate" && generatedB64) body.master_base64 = generatedB64;
      else if (mode === "upload" && masterFile) body.master_base64 = await fileToDataURL(masterFile);
      await axios.patch(`${API}/content/${editingId}`, body, auth);
      toast.success("Changes saved");
      setBuilderOpen(false);
      resetBuilder();
      fetchContent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save changes");
    } finally {
      setBusy(false);
    }
  };

  // No isDraft any more (G-15). `thenUseInCampaign` is what "Use in campaign" passes:
  // it saves to the library AND applies the result, so choosing it never means the
  // merchant has to think about storage as well (G-18).
  const save = async (_unused, { thenUseInCampaign = false } = {}) => {
    if (!name.trim()) { toast.error("Give your content a name"); return; }
    if (editingId) { await saveEdit(); return; }
    if (mode === "upload" && !masterFile) { toast.error("Choose an image to upload"); return; }
    if (mode === "generate" && !generatedB64) { toast.error("Generate an image first, then save"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      if (mode === "upload") {
        fd.append("file", masterFile);
        fd.append("source", "UPLOADED");
      } else {
        fd.append("file", dataURLtoBlob(generatedB64), "generated.png");
        fd.append("source", "AI_GENERATED");
        fd.append("ai_prompt", prompt.trim());
      }
      fd.append("name", name.trim());
      fd.append("fill_type", fillType);
      if (fillColor) fd.append("fill_color", fillColor);
      // Always false: nothing is a draft now, and the field stays on the model so an
      // existing draft record is untouched rather than migrated for a UI decision.
      fd.append("is_draft", "false");
      const res = await axios.post(`${API}/content/upload`, fd, auth);
      setBuilderOpen(false);
      resetBuilder();
      await fetchContent();
      if (thenUseInCampaign && onUseInCampaign && res.data) {
        // Saved first, then applied. If applying fails the creative still exists, which
        // is the right way round: losing the work would be worse than an unapplied
        // creative the merchant can pick manually.
        toast.success("Saved to your library and added to your campaign");
        onUseInCampaign(res.data);
        return;
      }
      toast.success("Saved to your library, ready to use in a campaign");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save content");
    } finally {
      setBusy(false);
    }
  };

  // Images upload (G-16). Straight to the library: a merchant importing a photo from
  // their device has already made the only decision that matters, so asking them to name
  // it first would put a form in front of a one-tap action. The file name becomes the
  // content name and can be edited afterwards like any other.
  const onGalleryUpload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { toast.error("Please choose an image"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source", "UPLOADED");
      fd.append("name", file.name.replace(/\.[^.]+$/, "") || "Uploaded image");
      fd.append("fill_type", fillType);
      fd.append("is_draft", "false");
      await axios.post(`${API}/content/upload`, fd, auth);
      toast.success("Uploaded to your library");
      await fetchContent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not upload that image");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this content and its renditions?")) return;
    try {
      await axios.delete(`${API}/content/${id}`, auth);
      toast.success("Content deleted");
      fetchContent();
    } catch {
      toast.error("Delete failed");
    }
  };


  // Everything, in one list. Drafts are gone (G-15) and any is_draft record already in
  // the database now shows here, which is where it was editable from anyway.
  const shown = content;
  const previewSrc = (mode === "generate" ? generatedB64 : masterPreview) || existingMaster;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold">{galleryOnly ? "Images" : "Content library"}</h2>
          {/* #3 asked what separates a draft from the library. The answer a merchant
              needs is not "private vs published" but whether a campaign can USE it,
              which is exactly what is_draft gates. */}
          <p className="text-sm text-muted-foreground">
            {galleryOnly
              ? "Make new images in Creative builder or import your own from your device."
              : "Ready to use. Anything here can be picked when you build a campaign."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* A gallery has nothing to switch and nothing to create. Both live in
              Creative builder, which the empty state points at. */}
          {/* Drafts removed entirely (round 4, G-15): content is editable from the
              library, so a separate drafts view was a second way to reach the same
              records. This also retires the J-3 explanation of draft-versus-library,
              by removing the distinction rather than explaining it. */}
          {!galleryOnly && (
            <Button onClick={openBuilder} data-testid="new-content-btn">
              <Plus className="mr-2 h-4 w-4" /> New content
            </Button>
          )}
          {/* Importing a photo is not "making" a creative, so Images gets its own
              upload rather than sending the merchant into the builder (G-16). */}
          {galleryOnly && (
            <label className="inline-flex">
              <Button asChild data-testid="gallery-upload-btn">
                <span className="cursor-pointer">
                  <Upload className="mr-2 h-4 w-4" /> Upload an image
                </span>
              </Button>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onGalleryUpload(e.target.files?.[0])}
              />
            </label>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : shown.length === 0 ? (
        <Card className="bg-card border-border border-dashed">
          <CardContent className="p-10 text-center">
            <ImageIcon className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm font-medium text-muted-foreground">
              {"No content yet"}
            </p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              {galleryOnly
                ? "Upload an image, or make one in Creative builder."
                : "Create content and it'll be ready for every placement."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
          {shown.map((c) => (
            <Card key={c.id} className="bg-card border-border overflow-hidden group">
              <div className="aspect-square bg-muted/40 flex items-center justify-center overflow-hidden cursor-pointer" onClick={() => openEditor(c)} title="Edit">
                {c.master_url ? (
                  <img src={resolveMedia(c.master_url)} alt={c.name} className="w-full h-full object-contain" />
                ) : <ImageIcon className="h-8 w-8 text-muted-foreground/40" />}
              </div>
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{c.name}</p>
                    {/* The placement count is gone (round 4, G-19). It was accurate,
                        which was the problem: old content has three renditions and new
                        content has two, so a correct number invited a question with no
                        useful answer. A merchant does not choose placements. The AI
                        marker stays, because that does tell them something. */}
                    {c.source === "AI_GENERATED" && (
                      <p className="text-[11px] text-muted-foreground">AI generated</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => openEditor(c)} title="Edit" className="text-muted-foreground hover:text-primary">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => remove(c.id)} title="Delete" className="text-muted-foreground hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {/* The publish action went with Drafts (G-15). Nothing is a draft
                    any more, so there is nothing to promote. */}
                {onUseInCampaign && (
                  <Button
                    size="sm"
                    className="w-full mt-2"
                    onClick={() => onUseInCampaign(c)}
                    data-testid={`use-in-campaign-${c.id}`}
                  >
                    Use in my campaign
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={builderOpen} onOpenChange={(o) => { setBuilderOpen(o); if (!o) { setExpandedPlacement(null); resetBuilder(); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit content" : "New content"}</DialogTitle>
            <DialogDescription>
              {/* Round 9, TP-12. The old line explained the mechanism, which is a thing
                  the merchant does not have to care about. Joel's wording, verbatim. */}
              {editingId
                ? "Update the name or padding fill, or replace the image. Renditions regenerate on save."
                : "Don't have your own creatives? Try Booma's AI generator."}
            </DialogDescription>
          </DialogHeader>

          <div className="flex rounded-md border border-border overflow-hidden text-sm w-fit">
            <button className={`px-4 py-2 flex items-center gap-2 ${mode === "upload" ? "bg-primary text-primary-foreground" : ""}`} onClick={() => setMode("upload")}>
              <Upload className="h-4 w-4" /> Upload
            </button>
            <button className={`px-4 py-2 flex items-center gap-2 ${mode === "generate" ? "bg-primary text-primary-foreground" : ""}`} onClick={() => setMode("generate")}>
              <Sparkles className="h-4 w-4" /> Generate
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-2">
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Summer burger promo" data-testid="content-name" />
              </div>

              {mode === "upload" ? (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Image</Label>
                  <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickFile} />
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="w-full h-28 rounded-md border border-dashed border-border flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-primary/50"
                    data-testid="content-file-btn"
                  >
                    <UploadCloud className="h-6 w-6" />
                    <span className="text-xs">{masterFile ? masterFile.name : (editingId ? "Replace image (optional)" : "Choose an image")}</span>
                  </button>
                  <p className="text-[11px] text-muted-foreground/70">Use a high-resolution image (≥1080px) for the sharpest renditions.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Start from your own image (#1). In Generate mode rather than as a
                      third tab: Upload already means "use this as-is", and this is
                      generation that happens to begin with a picture. */}
                  <div className="rounded-md border border-border p-2 space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Start from an image (optional)</Label>
                    {referenceB64 ? (
                      <div className="flex items-center gap-2">
                        <img
                          src={referenceB64}
                          alt="Reference"
                          className="h-10 w-10 rounded object-contain bg-muted shrink-0"
                        />
                        <span className="text-xs truncate flex-1" title={referenceName}>{referenceName}</span>
                        <Button variant="ghost" size="sm" onClick={clearReference} data-testid="clear-reference">
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <label className="flex items-center gap-2 text-xs text-primary cursor-pointer hover:underline">
                        <Upload className="h-3.5 w-3.5" />
                        Choose an image
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => onPickReference(e.target.files?.[0])}
                          data-testid="content-reference"
                        />
                      </label>
                    )}
                    <p className="text-[11px] text-muted-foreground/70">
                      {referenceB64
                        ? "Your image is the starting point - say what you want changed about it."
                        : "Leave this empty to generate from your description alone."}
                    </p>
                  </div>
                  <Label className="text-xs text-muted-foreground">
                    {referenceB64 ? "What should the AI do with it?" : "Prompt"}
                  </Label>
                  <Textarea
                    spellCheck="true"
                    value={prompt}
                    onChange={(e) => { setPrompt(e.target.value); if (generatedB64) setGeneratedB64(null); }}
                    rows={3}
                    placeholder={referenceB64
                      ? "e.g. make this more creative, keep my product front and centre"
                      : "Describe the image to generate…"}
                    data-testid="content-prompt"
                  />
                  <Button variant="outline" className="w-full" onClick={onGenerate} disabled={busy || !prompt.trim()} data-testid="content-generate">
                    {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                    {generatedB64 ? "Try again" : referenceB64 ? "Create from my image" : "Generate preview"}
                  </Button>
                  <p className="text-[11px] text-muted-foreground/70">{busy ? "Generating… usually takes 20–30 seconds." : "Generate, review the placements, then save when you're happy."}</p>
                  {generatedB64 && (
                    <div className="space-y-1 pt-2 border-t border-border">
                      <Label className="text-xs text-muted-foreground">Refine - iterative, keeps the image</Label>
                      <div className="flex gap-2">
                        <Input
                          value={editInstruction}
                          onChange={(e) => setEditInstruction(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onEdit(); } }}
                          placeholder="e.g. move the lamp right, make the table red"
                          data-testid="content-edit"
                        />
                        <Button variant="outline" onClick={onEdit} disabled={busy || !editInstruction.trim()}>Apply</Button>
                      </div>
                      <p className="text-[11px] text-muted-foreground/70">Each change builds on the current image.</p>
                    </div>
                  )}
                </div>
              )}

              {/* The padding-fill picker was here: Blurred / Brand colour / Transparent,
                  applied to whatever space a rendition had left over.

                  Removed (round 2): "padding options still should not be there, should
                  not be user responsibility ... no blurring, coloured edges." A set is
                  now the square plus the square EXTENDED to portrait through the image
                  model, so in the normal case there is no leftover space to fill and
                  nothing to choose. Padding survives only as the fallback when an
                  extension fails, where the merchant has no useful opinion anyway. */}
              {/* Round 9, TP-13: "Remove altogether does not need to be there". It
                  described how the portrait gets built, which the merchant sees in the
                  preview tiles either way. The earlier version of this paragraph also
                  claimed the portrait was never padded while the tile beside it read
                  "extension failed", so its history is of saying more than it knew. */}
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Placement preview</Label>
              {/* `|| busy` so the panels appear the MOMENT the merchant presses
                  Generate, which is Robin's sequence: "from prompt click > 1:1 box shows
                  loading indicator > populates 1:1 preview box only > 9:16 shows loading
                  indicator > then populates". Gated on previewSrc alone, the whole grid
                  stayed hidden behind "Generate to preview" for the 20 to 30 seconds
                  that most needed a sign of life (SP-21). */}
              {(previewSrc || busy) ? (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    {PLACEMENTS.map((p) => {
                      // The portrait tile shows the REAL extension once it has been
                      // fetched. Before that it letterboxes the square and says so,
                      // rather than mocking up padding and letting that read as the
                      // output (#20).
                      const isPortrait = p.key === "PORTRAIT";
                      // Round 7, SP-21. The portrait tile used to fall back to
                      // previewSrc, which is the SQUARE - so a merchant watched a 9:16
                      // box fill with a letterboxed square and had every reason to read
                      // that as the output. Same complaint as round 2's #20, where a
                      // padded mock-up read as the real thing.
                      //
                      // Now: this tile shows ONLY its own image. Nothing, or a spinner,
                      // until the genuine 9:16 arrives.
                      const real = isPortrait ? portraitB64 : previewSrc;
                      // Each tile decides for itself, because the two finish at
                      // different times and that sequence is the point: square first,
                      // then portrait. A shared flag would hold the square back or
                      // release the portrait early.
                      const pending = isPortrait ? (!portraitB64 && (busy || extending)) : busy;
                      return (
                      <div key={p.key} className="space-y-1">
                        <div
                          className={`relative w-full rounded border border-border overflow-hidden flex items-center justify-center ${real ? "cursor-zoom-in" : "bg-muted/40"}`}
                          style={{ aspectRatio: `${p.w}/${p.h}` }}
                          onClick={() => real && setExpandedPlacement({ ...p, src: real, isReal: true })}
                          title={real ? "Click to expand" : undefined}
                        >
                          {real ? (
                            <img
                              src={real}
                              alt={p.label}
                              className="w-full h-full object-cover relative"
                            />
                          ) : (
                            // The spinner Robin asked for, in the panel rather than
                            // under it: "a round circle loading indicator in each panel
                            // whilst they're generating, instead of the sub-text".
                            <div className="flex flex-col items-center gap-1.5 text-muted-foreground">
                              {pending && <Loader2 className="h-5 w-5 animate-spin" />}
                              <span className="text-[10px]">
                                {pending ? "Generating" : "Waiting"}
                              </span>
                            </div>
                          )}
                        </div>
                        <p className="text-[10px] text-muted-foreground text-center">{p.label}</p>
                        {/* Round 9, TP-14: "Extended from your square" is gone, and so
                            is "Generated to match". Both describe HOW a portrait that
                            came out fine was built, which is Booma's business and not
                            the merchant's, and the tile shows the result anyway.
                            The padded line stays: that one is a FAILURE, and a merchant
                            looking at a letterboxed portrait deserves to know why
                            rather than concluding the product is poor. */}
                        {isPortrait && portraitB64 && portraitPadded && (
                          <p className="text-[10px] text-center" style={{ color: "#b06a00" }}>
                            Padded, could not build a portrait
                          </p>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="h-28 rounded-md border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground">
                  {mode === "generate" ? "Generate to preview" : "Choose an image to preview"}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            {/* Two actions, not one (G-18). "Use in campaign" saves to the library as
                well, so choosing it never means losing the creative - the merchant
                should not have to think about storage to use what they just made.
                Offered only when the builder was opened FROM a campaign, because
                otherwise there is no campaign to apply it to. */}
            <Button
              variant={onUseInCampaign ? "outline" : "default"}
              onClick={() => save(false)}
              disabled={busy || (!editingId && mode === "generate" && !generatedB64)}
              data-testid="content-save"
              title="Adds it to your library so campaigns can use it."
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {editingId ? "Save changes" : "Save to library"}
            </Button>
            {!editingId && onUseInCampaign && (
              <Button
                onClick={() => save(false, { thenUseInCampaign: true })}
                disabled={busy || (mode === "generate" && !generatedB64)}
                data-testid="content-save-and-use"
                title="Saves it to your library and applies it to this campaign."
              >
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Use in campaign
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expand a placement preview to inspect the padding/fit */}
      <Dialog open={!!expandedPlacement} onOpenChange={(o) => !o && setExpandedPlacement(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-3xl flex flex-col items-center">
          <DialogHeader className="w-full">
            <DialogTitle>{expandedPlacement?.label}</DialogTitle>
            {/* Said "Padded preview" whatever it was showing, which is the impression
                #20 was raised about. It now says which of the two this is. */}
            <DialogDescription>
              {expandedPlacement?.isReal
                ? "The rendition that will be saved."
                : "Your image, fitted to this placement. Saving builds the real rendition."}
            </DialogDescription>
          </DialogHeader>
          {expandedPlacement && (expandedPlacement.src || previewSrc) && (
            <div
              className="relative rounded border border-border overflow-hidden flex items-center justify-center bg-muted"
              style={{
                ...(expandedPlacement.w >= expandedPlacement.h ? { width: "min(80vw, 820px)" } : { height: "min(70vh, 760px)" }),
                aspectRatio: `${expandedPlacement.w}/${expandedPlacement.h}`,
              }}
            >
              <img
                src={expandedPlacement.src || previewSrc}
                alt={expandedPlacement.label}
                className={expandedPlacement.isReal
                  ? "w-full h-full object-cover relative"
                  : "max-w-full max-h-full object-contain relative"}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
